/**
 * frameSharpness — Laplacian-variance blur detection for captured frames.
 *
 * Downsamples the capture canvas to 128×96, converts to grayscale,
 * applies a 3×3 Laplacian kernel, and computes variance of the result.
 * High variance → sharp image, low variance → blurry image.
 *
 * At 128×96 = 12,288 pixels — sub-millisecond on modern mobile devices.
 */

const SHARP_WIDTH = 128
const SHARP_HEIGHT = 96

/** Lazily-created sharpness canvas (reused across calls). */
let sharpCanvas: HTMLCanvasElement | null = null
let sharpCtx: CanvasRenderingContext2D | null = null

function getSharpCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (!sharpCanvas) {
    sharpCanvas = document.createElement('canvas')
    sharpCanvas.width = SHARP_WIDTH
    sharpCanvas.height = SHARP_HEIGHT
    sharpCtx = sharpCanvas.getContext('2d', { willReadFrequently: true })!
  }
  return { canvas: sharpCanvas, ctx: sharpCtx! }
}

export interface SharpnessResult {
  /** Laplacian variance — higher = sharper. Typically 0–2000+. */
  score: number
  /** Whether the frame is considered blurry (score < threshold). */
  isBlurry: boolean
}

/**
 * Compute sharpness of the current canvas content using Laplacian variance.
 *
 * @param sourceCanvas - The capture canvas (any size, will be downsampled to 128×96)
 * @param threshold - Score below which a frame is considered blurry (default 100)
 * @returns SharpnessResult with score and isBlurry flag
 */
export function computeSharpness(
  sourceCanvas: HTMLCanvasElement,
  threshold: number = 100,
): SharpnessResult {
  const { ctx } = getSharpCanvas()

  // Downsample source canvas → 128×96
  ctx.drawImage(sourceCanvas, 0, 0, SHARP_WIDTH, SHARP_HEIGHT)
  const imageData = ctx.getImageData(0, 0, SHARP_WIDTH, SHARP_HEIGHT)
  const data = imageData.data

  // Convert to grayscale array
  const w = SHARP_WIDTH
  const h = SHARP_HEIGHT
  const gray = new Float32Array(w * h)
  for (let i = 0; i < w * h; i++) {
    const offset = i * 4
    // Luminance: 0.299R + 0.587G + 0.114B
    gray[i] = 0.299 * data[offset] + 0.587 * data[offset + 1] + 0.114 * data[offset + 2]
  }

  // Apply 3×3 Laplacian kernel:
  //   0  1  0
  //   1 -4  1
  //   0  1  0
  // Skip border pixels (1px border)
  let sum = 0
  let sumSq = 0
  let count = 0

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x
      const laplacian =
        gray[idx - w] +          // top
        gray[idx - 1] +          // left
        -4 * gray[idx] +         // center
        gray[idx + 1] +          // right
        gray[idx + w]            // bottom

      sum += laplacian
      sumSq += laplacian * laplacian
      count++
    }
  }

  // Variance = E[X²] - E[X]²
  const mean = sum / count
  const variance = sumSq / count - mean * mean
  const score = Math.abs(variance)

  return {
    score,
    isBlurry: score < threshold,
  }
}
